import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Radar, Bar, Doughnut } from 'react-chartjs-2';
import { Target, Brain, Crown, ArrowRight } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';

interface LeadershipProfileData {
  traits: Array<{
    name: string;
    selfScore: number;
    evaluatorScore: number;
  }>;
  keyInsights: string[];
  recommendations: string[];
}

interface DevelopmentPlanData {
  shortTerm: Array<{
    goal: string;
    action: string;
  }>;
  longTerm: Array<{
    goal: string;
    action: string;
  }>;
}

interface EmotionalIntelligenceData {
  categories: string[];
  scores: number[];
  analysis: Array<{
    category: string;
    text: string;
  }>;
  developmentAreas: string[];
}

interface ExecutivePresenceData {
  components: Array<{
    name: string;
    score: number;
    percentage: number;
  }>;
  observations: Array<{
    text: string;
  }>;
  strategies: string[];
}

// Emotional Intelligence Data
const defaultEmotionalIntelligenceData = null;

// Default to null instead of hardcoded data
const defaultExecutivePresenceData = null;

// Decision Making Data
const decisionMakingData = {
  selfAssessment: [4.0, 3.7, 4.3, 3.5],
  evaluatorScores: [3.8, 3.5, 4.6, 3.0],
  traits: ["Logical Thinking", "Risk Assessment", "Adaptability", "Consensus Building"]
};

// Development Plan Data
const developmentPlanData = {
  shortTerm: [
    { goal: "Enhance delegation skills", action: "Participate in a delegation workshop." },
    { goal: "Improve non-verbal communication", action: "Practice body language in presentation rehearsals." }
  ],
  longTerm: [
    { goal: "Develop a personal leadership philosophy", action: "Engage in executive coaching sessions." },
    { goal: "Foster collaborative decision-making", action: "Host regular team brainstorming sessions." }
  ]
};

export default function LeadershipEdge() {
  const [activeCardIndex, setActiveCardIndex] = React.useState(0);
  const [showMorePresence, setShowMorePresence] = React.useState(false);
  const [leadershipProfile, setLeadershipProfile] = useState<LeadershipProfileData | null>(null);
  const [emotionalIntelligenceData, setEmotionalIntelligenceData] = useState<EmotionalIntelligenceData | null>(null);
  const [executivePresenceData, setExecutivePresenceData] = useState<ExecutivePresenceData | null>(null);
  const [developmentPlanData, setDevelopmentPlanData] = useState<DevelopmentPlanData | null>(null);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    leadershipProfile: false,
    emotionalIntelligence: false,
    developmentPlan: false
  });

  useEffect(() => {
    async function loadLeadershipProfile() {
      if (!currentUser) return;

      try {
        const reportRef = doc(db, collections.reports, currentUser.uid);
        const reportDoc = await getDoc(reportRef);

        if (reportDoc.exists()) {
          const data = reportDoc.data();
          
          if (data.leadership_edge_leadership_profile) {
            const profileData = JSON.parse(data.leadership_edge_leadership_profile);
            setLeadershipProfile(profileData.leadershipProfile);
          }
          
          if (data.leadership_edge_eq) {
            const eqData = JSON.parse(data.leadership_edge_eq);
            setEmotionalIntelligenceData(eqData);
          }
          
          if (data.leadership_edge_executive_presence) {
            const presenceData = JSON.parse(data.leadership_edge_executive_presence);
            setExecutivePresenceData(presenceData);
          }
          
          if (data.leadership_edge_plan) {
            const planData = JSON.parse(data.leadership_edge_plan);
            setDevelopmentPlanData(planData);
          }
        }
      } catch (error) {
        console.error('Error loading leadership profile:', error);
      } finally {
        setLoading(false);
      }
    }

    loadLeadershipProfile();
  }, [currentUser]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Leadership Profile Chart Configuration
  const leadershipProfileChartData = {
    labels: leadershipProfile?.traits.map(t => t.name) || [],
    datasets: [
      {
        label: 'Self Assessment',
        data: leadershipProfile?.traits.map(t => t.selfScore) || [],
        backgroundColor: 'rgba(238, 153, 234, 0.2)',
        borderColor: 'rgb(238, 153, 234)',
        borderWidth: 2,
        pointBackgroundColor: 'rgb(238, 153, 234)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgb(238, 153, 234)',
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: 'Evaluator Average',
        data: leadershipProfile?.traits.map(t => t.evaluatorScore) || [],
        backgroundColor: 'rgba(88, 211, 226, 0.2)',
        borderColor: 'rgb(88, 211, 226)',
        borderWidth: 2,
        pointBackgroundColor: 'rgb(88, 211, 226)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgb(88, 211, 226)',
        pointRadius: 4,
        pointHoverRadius: 6,
      }
    ]
  };

  const radarOptions = {
    scales: {
      r: {
        beginAtZero: true,
        max: 5,
        ticks: {
          stepSize: 1,
          backdropColor: 'transparent'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        angleLines: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        pointLabels: {
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 12
          }
        }
      }
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      }
    },
    maintainAspectRatio: false
  };

  const barOptions = {
    indexAxis: 'y' as const,
    scales: {
      x: {
        beginAtZero: true,
        max: 5,
        grid: {
          display: false
        }
      },
      y: {
        grid: {
          display: false
        }
      }
    },
    plugins: {
      legend: {
        display: false
      }
    },
    maintainAspectRatio: false
  };

  const donutOptions = {
    cutout: '60%',
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      }
    },
    maintainAspectRatio: false
  };

  return (
    <div className="bg-white">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-8"
      >
        {/* Leadership Profile Section */}
        <div className="mb-16">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Leadership Profile</h2>
          </div>
          
          <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
            <p className="text-gray-600 leading-relaxed mb-4">
              Explore your leadership traits, comparing self-assessment and evaluator feedback to uncover strengths and areas for growth.
            </p>
            
            <motion.div
              initial={false}
              animate={{ height: expandedSections.leadershipProfile ? 'auto' : 0, opacity: expandedSections.leadershipProfile ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="space-y-4 text-gray-600 leading-relaxed mb-4">
                <p>Gain a deeper understanding of your leadership qualities and potential:</p>
                <p><strong className="text-gray-900">Trait Analysis:</strong> Radar chart compares key traits like Vision, Adaptability, Decision-Making, Delegation, and Influence between your self-assessment and evaluator feedback.</p>
                <p><strong className="text-gray-900">Key Insights:</strong> Highlights traits where you excel, such as adaptability, and those needing improvement, like delegation.</p>
                <p><strong className="text-gray-900">Recommendations:</strong> Actionable steps to enhance leadership effectiveness, such as structured delegation practice or leading change initiatives.</p>
              </div>
            </motion.div>
            
            <button
              onClick={() => toggleSection('leadershipProfile')}
              className="text-primary hover:text-primary-hover font-medium flex items-center space-x-1"
            >
              <span>{expandedSections.leadershipProfile ? 'Read less' : 'Read more'}</span>
              <motion.svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                animate={{ rotate: expandedSections.leadershipProfile ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </motion.svg>
            </button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Trait Analysis</h3>
              {loading ? (
                <div className="h-[400px] flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
                </div>
              ) : !leadershipProfile ? (
                <div className="h-[400px] flex items-center justify-center">
                  <div className="text-center text-gray-600">
                    <p>Leadership profile data is not available yet.</p>
                    <p>Please check back once your report is generated.</p>
                  </div>
                </div>
              ) : (
                <div className="h-[400px]">
                  <Radar data={leadershipProfileChartData} options={radarOptions} />
                </div>
              )}
            </div>
            
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Insights</h3>
                {loading ? (
                  <div className="animate-pulse space-y-4">
                    <div className="h-20 bg-gray-200 rounded"></div>
                    <div className="h-20 bg-gray-200 rounded"></div>
                  </div>
                ) : !leadershipProfile ? (
                  <div className="text-center text-gray-600 p-4">
                    Key insights will be available once your report is generated.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {leadershipProfile.keyInsights.map((insight, index) => (
                      <div key={index} className="bg-white/50 backdrop-blur-sm rounded-lg p-4">
                        <p className="text-gray-600">{insight}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recommendations</h3>
                {loading ? (
                  <div className="animate-pulse space-y-3">
                    <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-6 bg-gray-200 rounded w-2/3"></div>
                  </div>
                ) : !leadershipProfile ? (
                  <div className="text-center text-gray-600">
                    Recommendations will be available once your report is generated.
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {leadershipProfile.recommendations.map((recommendation, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <span className="text-primary mt-1">•</span>
                        <span className="text-gray-600">{recommendation}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Emotional Intelligence Section */}
        <div className="mb-16">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Emotional Intelligence Insights</h2>
          </div>
          
          <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
            <p className="text-gray-600 leading-relaxed mb-4">
              Assess your emotional intelligence across five core components and uncover strategies for personal and professional growth.
            </p>
            
            <motion.div
              initial={false}
              animate={{ height: expandedSections.emotionalIntelligence ? 'auto' : 0, opacity: expandedSections.emotionalIntelligence ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="space-y-4 text-gray-600 leading-relaxed mb-4">
                <p>Emotional intelligence is essential for effective leadership. This section provides an in-depth look at your EQ:</p>
                <p><strong className="text-gray-900">EQ Components:</strong> Bar chart evaluates Self-Awareness, Self-Regulation, Empathy, Motivation, and Social Skills.</p>
                <p><strong className="text-gray-900">Analysis:</strong> Highlights strengths like empathy and areas for improvement such as self-regulation.</p>
                <p><strong className="text-gray-900">Development Areas:</strong> Provides strategies to strengthen EQ, such as mindfulness exercises for better self-regulation or active listening to enhance social skills.</p>
              </div>
            </motion.div>
            
            <button
              onClick={() => toggleSection('emotionalIntelligence')}
              className="text-primary hover:text-primary-hover font-medium flex items-center space-x-1"
            >
              <span>{expandedSections.emotionalIntelligence ? 'Read less' : 'Read more'}</span>
              <motion.svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                animate={{ rotate: expandedSections.emotionalIntelligence ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </motion.svg>
            </button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">EQ Components</h3>
              {!emotionalIntelligenceData ? (
                <div className="h-[400px] flex items-center justify-center">
                  <div className="text-center text-gray-600">
                    <p>Emotional intelligence data is not available yet.</p>
                    <p>Please check back once your report is generated.</p>
                  </div>
                </div>
              ) : (
                <div className="h-[400px]">
                  <Bar data={{
                    labels: emotionalIntelligenceData.categories,
                    datasets: [{
                      data: emotionalIntelligenceData.scores,
                      backgroundColor: [
                        'rgba(238, 153, 234, 0.7)',
                        'rgba(88, 211, 226, 0.7)',
                        'rgba(249, 197, 95, 0.7)',
                        'rgba(75, 192, 192, 0.7)',
                        'rgba(153, 102, 255, 0.7)'
                      ],
                      borderColor: 'white',
                      borderWidth: 2,
                      borderRadius: 4
                    }]
                  }} options={barOptions} />
                </div>
              )}
            </div>
            
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Analysis</h3>
                {!emotionalIntelligenceData ? (
                  <div className="text-center text-gray-600 p-4">
                    <p>Analysis will be available once your report is generated.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {emotionalIntelligenceData.analysis.map((item, index) => (
                      <div key={index} className="bg-white/50 backdrop-blur-sm rounded-lg p-4">
                        <p className="text-gray-600">{item.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Development Areas</h3>
                {!emotionalIntelligenceData ? (
                  <div className="text-center text-gray-600">
                    <p>Development areas will be available once your report is generated.</p>
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {emotionalIntelligenceData.developmentAreas.map((area, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <span className="text-secondary mt-1">•</span>
                        <span className="text-gray-600">{area}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Executive Presence Section */}
        <div className="mb-16">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Executive Presence Analysis</h2>
          </div>
           
          <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
            <p className="text-gray-600 leading-relaxed mb-4">
              This section evaluates three key components of executive presence: Confidence, Communication Clarity, and Non-Verbal Communication. These traits reflect how you project authority, articulate ideas, and reinforce your message through body language.
            </p>
            
            <motion.div
              initial={false}
              animate={{ height: showMorePresence ? 'auto' : 0, opacity: showMorePresence ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="space-y-4 text-gray-600 leading-relaxed mb-4">
                <p>
                  <strong className="text-gray-900">Confidence:</strong> Your ability to convey self-assurance and decisiveness, inspiring trust and credibility.
                </p>
                <p>
                  <strong className="text-gray-900">Communication Clarity:</strong> How effectively you express ideas to ensure understanding and influence.
                </p>
                <p>
                  <strong className="text-gray-900">Non-Verbal Communication:</strong> The impact of body language, eye contact, and posture in reinforcing your presence.
                </p>
                <p className="mt-6">
                  The pie chart shows the relative emphasis on each trait. Larger segments represent strengths, while smaller ones highlight areas for growth. Balance isn't always necessary—focus on enhancing any weaker traits while leveraging your strengths.
                </p>
                <p>
                  Use these insights to refine your presence and adapt your approach to various situations, ensuring you make a strong and lasting impression.
                </p>
              </div>
            </motion.div>
            
            <button
              onClick={() => setShowMorePresence(!showMorePresence)}
              className="text-primary hover:text-primary-hover font-medium flex items-center space-x-1"
            >
              <span>{showMorePresence ? 'Read less' : 'Read more'}</span>
              <motion.svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                animate={{ rotate: showMorePresence ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </motion.svg>
            </button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Component Distribution</h3>
              {!executivePresenceData ? (
                <div className="h-[400px] flex items-center justify-center">
                  <div className="text-center text-gray-600">
                    <p>Executive presence data is not available yet.</p>
                    <p>Please check back once your report is generated.</p>
                  </div>
                </div>
              ) : (
                <div className="h-[400px]">
                  <Doughnut 
                    data={{
                      labels: executivePresenceData.components.map(c => c.name),
                      datasets: [{
                        data: executivePresenceData.components.map(c => c.percentage),
                        backgroundColor: [
                          'rgba(238, 153, 234, 0.8)',
                          'rgba(88, 211, 226, 0.8)',
                          'rgba(249, 197, 95, 0.8)'
                        ],
                        borderColor: 'white',
                        borderWidth: 2
                      }]
                    }} 
                    options={donutOptions} 
                  />
                </div>
              )}
            </div>
            
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Observations</h3>
                {!executivePresenceData ? (
                  <div className="text-center text-gray-600 p-4">
                    <p>Key observations will be available once your report is generated.</p>
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {executivePresenceData.observations.map((observation, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <span className="text-primary mt-1">•</span>
                        <span className="text-gray-600">{observation.text}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Enhancement Strategies</h3>
                {!executivePresenceData ? (
                  <div className="text-center text-gray-600">
                    <p>Enhancement strategies will be available once your report is generated.</p>
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {executivePresenceData.strategies.map((strategy, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <span className="text-[#f9c55f] mt-1">•</span>
                        <span className="text-gray-600">{strategy}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Development Action Plan */}
        <div>
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Leadership Development Action Plan</h2>
          </div>
          
          <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
            <p className="text-gray-600 leading-relaxed mb-4">
              Map out your leadership development journey with a clear action plan for short-term and long-term growth.
            </p>
            
            <motion.div
              initial={false}
              animate={{ height: expandedSections.developmentPlan ? 'auto' : 0, opacity: expandedSections.developmentPlan ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="space-y-4 text-gray-600 leading-relaxed mb-4">
                <p>Take actionable steps to reach your leadership potential:</p>
                <p><strong className="text-gray-900">Short-term Goals:</strong> Focused on immediate improvements, such as delegation workshops and practicing non-verbal communication.</p>
                <p><strong className="text-gray-900">Long-term Goals:</strong> Ambitious objectives like developing a personal leadership philosophy or fostering collaborative decision-making through team sessions.</p>
                <p><strong className="text-gray-900">Personalized Recommendations:</strong> Each goal is paired with practical actions to ensure measurable progress.</p>
              </div>
            </motion.div>
            
            <button
              onClick={() => toggleSection('developmentPlan')}
              className="text-primary hover:text-primary-hover font-medium flex items-center space-x-1"
            >
              <span>{expandedSections.developmentPlan ? 'Read less' : 'Read more'}</span>
              <motion.svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                animate={{ rotate: expandedSections.developmentPlan ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </motion.svg>
            </button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Short-term Goals</h3>
              <div className="space-y-4">
                {!developmentPlanData ? (
                  <div className="text-center text-gray-600 p-4">
                    <p>Short-term goals will be available once your report is generated.</p>
                  </div>
                ) : (
                  developmentPlanData.shortTerm.map((item, index) => (
                  <div key={index} className="bg-white/50 backdrop-blur-sm rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">{item.goal}</h4>
                    <p className="text-gray-600">{item.action}</p>
                  </div>
                  ))
                )}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Long-term Goals</h3>
              <div className="space-y-4">
                {!developmentPlanData ? (
                  <div className="text-center text-gray-600 p-4">
                    <p>Long-term goals will be available once your report is generated.</p>
                  </div>
                ) : (
                  developmentPlanData.longTerm.map((item, index) => (
                  <div key={index} className="bg-white/50 backdrop-blur-sm rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">{item.goal}</h4>
                    <p className="text-gray-600">{item.action}</p>
                  </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}